import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/pages/home/components/Footer';

const SITE_URL = 'https://oopbuyspread.mom';
const LOGO_URL = 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/5133451b25e408e4a25e4df97e05add8.jpeg';
const ARTICLE_IMAGE = 'https://readdy.ai/api/search-image?query=Clean%20minimal%20warehouse%20logistics%20scene%20with%20cardboard%20boxes%20on%20wooden%20table%20soft%20natural%20daylight%20streaming%20through%20window%20neutral%20cream%20background%20scandinavian%20aesthetic%20editorial%20product%20photography%20warm%20tones%20organized%20composition%20shipping%20and%20packaging%20concept&width=1600&height=600&seq=article-hero-shipping-v2&orientation=landscape';

const articleSchema = {
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: 'Oopbuy Spreadsheet Versand Guide: Alles über Shipping &amp; Lieferung',
  description: 'Alles über den Versand mit Oopbuy Spreadsheet. Lieferoptionen, Tracking, internationale Sendungen und Tipps für schnelleren Versand.',
  image: ARTICLE_IMAGE,
  datePublished: '2026-07-15',
  dateModified: '2026-07-15',
  author: {
    '@type': 'Organization',
    name: 'Oopbuy Spreadsheet',
    url: SITE_URL,
  },
  publisher: {
    '@type': 'Organization',
    name: 'Oopbuy Spreadsheet',
    logo: {
      '@type': 'ImageObject',
      url: LOGO_URL,
    },
  },
  mainEntityOfPage: {
    '@type': 'WebPage',
    '@id': `${SITE_URL}/artikel/oopbuy-spreadsheet-shipping-guide`,
  },
  inLanguage: 'de',
};

export default function ShippingGuidePage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <main>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }} />
      <Navbar />

      <article className="pt-[72px]">
        <div className="px-4 md:px-8 lg:px-20 py-6">
          <nav className="flex items-center gap-2 text-sm text-foreground-500">
            <Link to="/" className="hover:text-foreground-900 transition-colors">Home</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-900">Oopbuy Spreadsheet Versand Guide</span>
          </nav>
        </div>

        <div className="w-full h-[300px] md:h-[420px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Clean%20minimal%20warehouse%20logistics%20scene%20with%20cardboard%20boxes%20on%20wooden%20table%20soft%20natural%20daylight%20streaming%20through%20window%20neutral%20cream%20background%20scandinavian%20aesthetic%20editorial%20product%20photography%20warm%20tones%20organized%20composition%20shipping%20and%20packaging%20concept&width=1600&height=600&seq=article-hero-shipping-v2&orientation=landscape"
            alt="Oopbuy Spreadsheet Versand Guide"
            className="w-full h-full object-cover object-top"
          />
        </div>

        <div className="max-w-[780px] mx-auto px-4 md:px-8 py-12 md:py-16">
          <h1 className="text-[32px] md:text-[42px] font-heading font-bold text-foreground-950 leading-tight mb-8 text-center">
            Oopbuy Spreadsheet Versand Guide: Alles über Shipping & Lieferung
          </h1>

          <p className="text-lg leading-[1.8] text-foreground-700 mb-8">
            Der Versandprozess ist einer der wichtigsten Aspekte beim Online-Shopping. Mit <strong>Oopbuy Spreadsheet</strong> hast du nicht nur deine Einkäufe im Griff, sondern auch den kompletten Versandprozess. In diesem ausführlichen <strong>Oopbuy Spreadsheet Versand Guide</strong> erfährst du alles, was du über Lieferoptionen, Tracking und internationale Sendungen wissen musst.
          </p>

          <p className="text-lg leading-[1.8] text-foreground-700 mb-8">
            Zurück zur <Link to="/" className="text-primary-600 hover:text-primary-700 underline">Oopbuy Spreadsheet Startseite</Link> – hier findest du eine Übersicht aller Kategorien und Funktionen.
          </p>

          <h2 className="text-[26px] md:text-[30px] font-heading font-bold text-foreground-950 mt-12 mb-6">
            Wie funktioniert der Versand mit Oopbuy Spreadsheet?
          </h2>
          <p className="text-lg leading-[1.8] text-foreground-700 mb-6">
            <strong>Oopbuy Spreadsheet</strong> bietet eine nahtlose Integration aller Versandinformationen direkt in dein persönliches Spreadsheet. Sobald eine Bestellung aufgegeben wird, werden automatisch die entsprechenden Versanddaten in dein <strong>Oopbuy Spreadsheet</strong> eingetragen. Du siehst auf einen Blick: Versandstatus, Tracking-Nummer, voraussichtliches Lieferdatum und den aktuellen Standort deines Pakets.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div className="bg-secondary-50 rounded-xl p-6">
              <div className="w-10 h-10 flex items-center justify-center mb-3">
                <i className="ri-truck-line text-2xl text-primary-500"></i>
              </div>
              <h3 className="text-lg font-semibold text-foreground-900 mb-2">Nationaler Versand</h3>
              <p className="text-sm text-foreground-600 leading-relaxed"><strong>Oopbuy Spreadsheet</strong> unterstützt alle gängigen deutschen Versanddienstleister. Lieferzeiten von 1-3 Werktagen sind der Standard.</p>
            </div>
            <div className="bg-secondary-50 rounded-xl p-6">
              <div className="w-10 h-10 flex items-center justify-center mb-3">
                <i className="ri-global-line text-2xl text-primary-500"></i>
              </div>
              <h3 className="text-lg font-semibold text-foreground-900 mb-2">Internationaler Versand</h3>
              <p className="text-sm text-foreground-600 leading-relaxed">Mit <strong>Oopbuy Spreadsheet</strong> kannst du Sendungen in über 50 Länder verfolgen. Zollinformationen werden automatisch angezeigt.</p>
            </div>
            <div className="bg-secondary-50 rounded-xl p-6">
              <div className="w-10 h-10 flex items-center justify-center mb-3">
                <i className="ri-map-pin-line text-2xl text-primary-500"></i>
              </div>
              <h3 className="text-lg font-semibold text-foreground-900 mb-2">Live-Tracking</h3>
              <p className="text-sm text-foreground-600 leading-relaxed">Verfolge deine Bestellungen in Echtzeit direkt in deinem <strong>Oopbuy Spreadsheet</strong>. Kein Wechseln zwischen verschiedenen Tracking-Seiten nötig.</p>
            </div>
            <div className="bg-secondary-50 rounded-xl p-6">
              <div className="w-10 h-10 flex items-center justify-center mb-3">
                <i className="ri-secure-payment-line text-2xl text-primary-500"></i>
              </div>
              <h3 className="text-lg font-semibold text-foreground-900 mb-2">Versicherter Versand</h3>
              <p className="text-sm text-foreground-600 leading-relaxed">Alle Sendungen über <strong>Oopbuy Spreadsheet</strong> sind automatisch versichert. Im Schadensfall ist deine Bestellung abgesichert.</p>
            </div>
          </div>

          <h2 className="text-[26px] md:text-[30px] font-heading font-bold text-foreground-950 mt-12 mb-6">
            Versandoptionen im Überblick
          </h2>

          <div className="overflow-x-auto mb-8">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-secondary-50">
                  <th className="text-left p-4 font-semibold text-foreground-900 rounded-tl-lg">Versandart</th>
                  <th className="text-left p-4 font-semibold text-foreground-900">Lieferzeit</th>
                  <th className="text-left p-4 font-semibold text-foreground-900">Kosten</th>
                  <th className="text-left p-4 font-semibold text-foreground-900 rounded-tr-lg">Tracking</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-foreground-200/50">
                  <td className="p-4 text-foreground-900 font-medium">Standardversand</td>
                  <td className="p-4 text-foreground-700">3-5 Werktage</td>
                  <td className="p-4 text-primary-600 font-medium">Kostenlos</td>
                  <td className="p-4 text-foreground-700"><i className="ri-check-line text-primary-500 mr-1"></i> Inklusive</td>
                </tr>
                <tr className="border-b border-foreground-200/50">
                  <td className="p-4 text-foreground-900 font-medium">Expressversand</td>
                  <td className="p-4 text-foreground-700">1-2 Werktage</td>
                  <td className="p-4 text-foreground-700">ab 4,99 €</td>
                  <td className="p-4 text-foreground-700"><i className="ri-check-line text-primary-500 mr-1"></i> Inklusive</td>
                </tr>
                <tr>
                  <td className="p-4 text-foreground-900 font-medium rounded-bl-lg">Overnight Express</td>
                  <td className="p-4 text-foreground-700">Nächster Werktag</td>
                  <td className="p-4 text-foreground-700">ab 9,99 €</td>
                  <td className="p-4 text-foreground-700 rounded-br-lg"><i className="ri-check-line text-primary-500 mr-1"></i> Inklusive</td>
                </tr>
              </tbody>
            </table>
          </div>

          <h2 className="text-[26px] md:text-[30px] font-heading font-bold text-foreground-950 mt-12 mb-6">
            Tipps für schnelleren Versand
          </h2>
          <ul className="space-y-4 mb-8">
            <li className="flex items-start gap-3">
              <i className="ri-checkbox-circle-fill text-primary-500 text-xl mt-0.5 flex-shrink-0"></i>
              <div>
                <strong className="text-foreground-900">Bestellungen vor 14 Uhr:</strong>
                <span className="text-foreground-700"> Bestellungen, die vor 14 Uhr in deinem <strong>Oopbuy Spreadsheet</strong> erfasst werden, gehen noch am selben Tag raus.</span>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <i className="ri-checkbox-circle-fill text-primary-500 text-xl mt-0.5 flex-shrink-0"></i>
              <div>
                <strong className="text-foreground-900">Sammelbestellungen:</strong>
                <span className="text-foreground-700"> Mit <strong>Oopbuy Spreadsheet</strong> kannst du mehrere Bestellungen bündeln und Versandkosten sparen.</span>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <i className="ri-checkbox-circle-fill text-primary-500 text-xl mt-0.5 flex-shrink-0"></i>
              <div>
                <strong className="text-foreground-900">Adressprüfung:</strong>
                <span className="text-foreground-700"> <strong>Oopbuy Spreadsheet</strong> prüft automatisch deine Lieferadresse auf Richtigkeit, um Verzögerungen zu vermeiden.</span>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <i className="ri-checkbox-circle-fill text-primary-500 text-xl mt-0.5 flex-shrink-0"></i>
              <div>
                <strong className="text-foreground-900">Versandbenachrichtigungen:</strong>
                <span className="text-foreground-700"> Aktiviere Push-Benachrichtigungen in <strong>Oopbuy Spreadsheet</strong> und verpasse kein Status-Update.</span>
              </div>
            </li>
          </ul>

          <div className="bg-primary-50 rounded-2xl p-8 md:p-10 text-center my-12">
            <h2 className="text-[24px] md:text-[28px] font-heading font-bold text-foreground-950 mb-3">
              Bereit für schnellen Versand mit Oopbuy Spreadsheet?
            </h2>
            <p className="text-foreground-600 mb-6 max-w-md mx-auto">
              Verwalte deine Bestellungen und verfolge den Versand – alles in einem <strong>Oopbuy Spreadsheet</strong>.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                Oopbuy Spreadsheet
                <i className="ri-arrow-right-up-line text-sm"></i>
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-full border-2 border-primary-300 text-primary-700 text-sm font-semibold hover:bg-primary-100 transition-colors whitespace-nowrap cursor-pointer"
              >
                Buy It Now
                <i className="ri-shopping-bag-line text-sm"></i>
              </a>
            </div>
          </div>

          <div className="border-t border-foreground-200/50 pt-8 mt-8">
            <p className="text-sm text-foreground-500">
              <i className="ri-arrow-left-line mr-1"></i>
              Zurück zur <Link to="/" className="text-primary-600 hover:text-primary-700 underline font-medium">Oopbuy Spreadsheet Startseite</Link> – entdecke alle Guides und Kategorien.
            </p>
          </div>
        </div>
      </article>

      <Footer />
    </main>
  );
}